// Movement
export const MOVEMENT = {
  DASH_SPEED: 3,            // px/frame (Melee: dash speed = ground movement speed)
  TILT_RATE: 0.15,          // visual roll per frame while moving (Melee: tilt = angled input)
  MAX_CLIMBABLE_RISE: 12,   // max terrain rise over 1 move step (~76°)
  MIN_STEEP_SPEED_MULT: 0.2, // speed floor on very steep walkable slopes
} as const;

// Traction — ground movement physics (probe-and-snap: collision + downward scan)
export const TRACTION = {
  CATCHUP_FRAMES: 10,         // frames of smooth catchup after walk stops
  GROUND_STICK: 2,            // px downward probe distance for terrain following
  WALK_WALL_NY: 0.3,          // walking wall threshold — slightly more permissive than SLOPE.WALL_NY (0.35) to handle jagged edges
} as const;

// Momentum — unified resource replacing stamina + turn timer
export const MOMENTUM = {
  MAX: 80,                     // base momentum granted each turn (nerfed from 100 — ~200px range)
  ABSOLUTE_MAX: 80,            // hard cap — momentum never exceeds 80
  CARRY_OVER_RATE: 0.3,       // fraction of unused momentum that carries to next turn (nerfed from 0.5)
  // Movement costs (lower than old stamina — momentum drains via baseline too)
  FLAT_COST_PER_FRAME: 2.5,   // 2x cost — wide stages need movement to be a real commitment
  UPHILL_COST_MAX: 2.8,       // cost multiplier at steepest climbable slope
  UPHILL_SPEED_PENALTY: 0.5,  // min speed fraction at steepest slope
  DOWNHILL_COST_MULT: 0.15,
  STARTUP_COST_MULT: 2.5,     // first frame from standstill (up from 2.0 — rewards staying put)
  // Drain — starts on first move, escalates on idle
  BASELINE_DRAIN: 0.5,        // per frame, starts on first move
  IDLE_SHIELD_FRAMES: 120,    // 2s before idle drain kicks in (Melee: shield = protection window)
  IDLE_DRAIN: 1.5,            // after grace period
  IDLE_FAST_FRAMES: 240,      // 4s before fast idle drain
  IDLE_FAST_DRAIN: 3.0,       // escalated idle drain
  // Special boost
  SPECIAL_BOOST: 0,           // momentum gained on special (B) press (disabled)
  UPHILL_BURST_COST: 20,      // momentum cost for upward burst directions
  // Backstop timer (hidden, replaces visible turn timer)
  BACKSTOP_SECONDS: 45,
  BACKSTOP_VISIBLE_AT: 10,    // show timer only in last 10s
  // Screen-space momentum bar
  BAR_WIDTH: 500,
  BAR_HEIGHT: 18,
  BAR_Y_OFFSET: 200,          // px from bottom of screen (just above player cards)
  COLOR_FULL: 0xffd700,       // gold (>50%)
  COLOR_MID: 0xff8800,        // orange (50-20%)
  COLOR_LOW: 0xff3333,        // red (<20%)
  COLOR_BONUS: 0x44ff88,      // green tint when above base MAX
  MID_THRESHOLD: 0.5,
  LOW_THRESHOLD: 0.2,
  // Aura — energy cape (drapes down when still, flows behind on movement)
  AURA_SPAWN_OFFSET_Y: 12,       // spawn below body center (cape hangs from bottom)
  AURA_DRAPE_SPEED: 1.4,         // idle: downward drape speed; moving: aim-back flow speed
  AURA_DRAPE_SWAY: 0.4,          // horizontal sway amplitude when stationary
  AURA_TRAIL_INHERIT: 0.9,       // how much of player velocity particles inherit (inverted)
  AURA_GRAVITY: 0.045,           // strong downward pull (elongates cape)
  AURA_FRICTION: 0.97,           // friction for natural settle
  AURA_FLOW_SPREAD: 15,          // lateral spread from spawn point
  // Layer 1: Dark backing — Sakurai "mix in darker elements" for depth/contrast
  AURA_DARK_COLOR: 0x1a0800,
  AURA_DARK_COUNT_MIN: 1,
  AURA_DARK_COUNT_MAX: 3,
  AURA_DARK_SCALE_MIN: 0.8,
  AURA_DARK_SCALE_MAX: 1.5,
  AURA_DARK_ALPHA: 0.3,
  AURA_DARK_LIFE_MIN: 0.25,
  AURA_DARK_LIFE_MAX: 0.45,
  // Layer 2: Cape flow — soft elongated wisps forming the flowing body
  AURA_FLOW_COLOR: 0xddaa00,
  AURA_FLOW_COUNT_MIN: 2,
  AURA_FLOW_COUNT_MAX: 6,
  AURA_FLOW_SCALE_MIN: 0.5,
  AURA_FLOW_SCALE_MAX: 1.2,
  AURA_FLOW_ALPHA_MIN: 0.15,
  AURA_FLOW_ALPHA_MAX: 0.35,
  AURA_FLOW_LIFE_MIN: 0.25,
  AURA_FLOW_LIFE_MAX: 0.45,
  // Layer 3: Crisp flecks — Sakurai "combine with crisp effects" (hard texture, sharp contrast)
  AURA_CRISP_COLOR: 0xffcc00,
  AURA_CRISP_COUNT_MIN: 0,
  AURA_CRISP_COUNT_MAX: 2,
  AURA_CRISP_SCALE_MIN: 0.3,
  AURA_CRISP_SCALE_MAX: 0.7,
  AURA_CRISP_ALPHA: 0.4,
  AURA_CRISP_LIFE_MIN: 0.08,
  AURA_CRISP_LIFE_MAX: 0.15,
  // Layer 4: Edge sparks — tiny ADD pops along the cape
  AURA_SPARK_COLOR: 0xfff4cc,
  AURA_SPARK_COUNT_MIN: 0,
  AURA_SPARK_COUNT_MAX: 2,
  AURA_SPARK_SCALE_MIN: 0.3,
  AURA_SPARK_SCALE_MAX: 0.6,
  AURA_SPARK_ALPHA: 0.2,
  AURA_SPARK_LIFE_MIN: 0.08,
  AURA_SPARK_LIFE_MAX: 0.18,
} as const;

// Climb visual feedback — dust burst + squash-stretch spring
export const CLIMB_FX = {
  // Dust burst
  RISE_THRESHOLD: 8.0,
  DUST_COOLDOWN: 4,
  DUST_DARK_COLOR: 0x1a1a2e,
  DUST_DARK_COUNT: 4,
  DUST_DARK_SIZE_MIN: 6,
  DUST_DARK_SIZE_MAX: 14,
  DUST_DARK_LIFETIME: 18,
  DUST_SPARK_COUNT: 6,
  DUST_SPARK_SIZE_MIN: 3,
  DUST_SPARK_SIZE_MAX: 5,
  DUST_SPARK_LIFETIME: 10,
  DUST_LINE_COUNT: 2,
  DUST_LINE_SIZE: 2,
  DUST_LINE_LIFETIME: 7,
  FIRST_BURST_SHAKE: 1.5,
  // Squash-stretch spring
  SPRING_STIFFNESS: 0.3,
  SPRING_DAMPING: 0.75,
  SQUASH_MIN: 0.7,
  SQUASH_MAX: 1.4,
  CLIMB_IMPULSE: 0.12,
  ARRIVE_IMPULSE: 0.15,
  DROP_IMPULSE: 0.10,
  LAND_IMPULSE: 0.18,
} as const;

// Hop & Lift — aerial movement system (grounded hop + airborne sustained lift)
export const HOP = {
  // Hop (grounded impulse)
  HOP_VY: -3.5,              // upward impulse (gravity=0.06 → ~102px peak, ~58 frames to apex)
  HOP_MOMENTUM_COST: 18,     // 3x cost — jumping is a real commitment

  // Lift (airborne sustained thrust)
  LIFT_THRUST: 0.12,         // per-frame magnitude (2× gravity)
  LIFT_UPWARD_BIAS: 0.25,    // radians (~14°) added toward up on horizontal input
  LIFT_MOMENTUM_DRAIN: 0.4,  // per-frame cost while lift active

  // Forgiveness
  COYOTE_FRAMES: 5,          // ~83ms after leaving ground where hop still works
  BUFFER_FRAMES: 5,          // ~83ms before landing where jump press is buffered

  // Fuel — max lift frames per airborne session, resets on landing
  MAX_LIFT_FUEL: 90,         // ~1.5s of lift per flight (60fps)

  // Speed cap
  MAX_LIFT_SPEED: 5.0,       // max speed from lift (burst=8.0, dash=3.0 for reference)

  // Activation burst (one-time impulse on lift OFF→ON)
  LIFT_BURST_SPEED: 2.0,           // one-time impulse magnitude (hop_vy=3.5, burst=8.0 for ref)
  LIFT_BURST_MOMENTUM_COST: 4,     // flat cost per activation burst

  // Ground proximity — spatial coyote for spiky terrain
  NEAR_GROUND_PX: 25,             // if feet within this distance of terrain below, hop instead of lift

  // Visual
  HOP_SQUASH: 0.80,          // squash spring on hop launch
  HOP_SQUASH_VEL: 0.20,      // spring velocity (stretch after crouch)
  LIFT_TRAIL_TIER_CRUISE: 2.0,       // speed threshold for cruise tier
  LIFT_TRAIL_TIER_FAST: 4.0,         // speed threshold for fast tier
  LIFT_TRAIL_COOLDOWN_HOVER: 3,      // frames between spawns (< cruise)
  LIFT_TRAIL_COOLDOWN_CRUISE: 2,     // frames between spawns (cruise-fast)
  LIFT_TRAIL_COOLDOWN_FAST: 1,       // frames between spawns (>= fast)
} as const;

// Pass-through platforms — one-way wireframe behavior
export const PLATFORM = {
  DROP_TIMER_FRAMES: 18,    // frames to ignore wireframe after drop-through (~300ms)
  DROP_INITIAL_VY: 3.0,     // downward kick on drop-through (must clear ECB_RADIUS + platform thickness before timer expires)
} as const;

// Fast Fall — Smash-style commit-to-falling with momentum reward
export const FAST_FALL = {
  KICK_VY: 2.0,              // Immediate downward velocity boost on activation
  GRAVITY_MULT: 2.0,         // Gravity multiplier while active (0.06 → 0.12)
  MAX_FALL_SPEED: 7.0,       // Increased terminal velocity (vs 4.0 normal)
  MOMENTUM_GRANT: 5,         // One-time momentum gain on activation
  TRAIL_COOLDOWN: 3,         // Frames between trail particle spawns
} as const;
